/**
 * Onboarding board queue / triage predicates.
 *
 * The board is a work queue. Closed applications (hired / withdrawn /
 * rejected / rescinded) are archive, not Active work.
 */

export const QUEUE_ACTIVE = 'active';
export const QUEUE_HIRED = 'hired';
export const QUEUE_ALL = 'all';

export const CLOSED_STATUSES = ['hired', 'withdrawn', 'rejected', 'rescinded'];

export const QUEUE_OPTIONS = [
  { id: QUEUE_ACTIVE, label: 'Active' },
  { id: QUEUE_HIRED, label: 'Hired' },
  { id: QUEUE_ALL, label: 'All' },
];

/**
 * Left-pane chrome per queue. Hired is a lookup archive, not a work stage —
 * the heading has to say that, because the short toggle label cannot.
 */
export const QUEUE_PANE = {
  [QUEUE_ACTIVE]: {
    title: 'Onboarding queue',
    hint: null,
    searchPlaceholder: 'Name or candidate ref…',
  },
  [QUEUE_HIRED]: {
    title: 'Hire archive',
    hint: 'Completed hire packets. Day-1 work lives on the employee record.',
    searchPlaceholder: 'Name or staff ID…',
  },
  [QUEUE_ALL]: {
    title: 'All records',
    hint: null,
    searchPlaceholder: 'Name, ref, or staff ID…',
  },
};

export const TRIAGE_ANY = 'any';

export const TRIAGE_OPTIONS = [
  { value: TRIAGE_ANY, label: 'All active work' },
  { value: 'awaiting_candidate', label: 'Awaiting candidate' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'ready_to_hire', label: 'Ready to hire' },
  { value: 'starting_this_week', label: 'Starting this week' },
];

export const isClosedStatus = (status) => CLOSED_STATUSES.includes(status);

export const isHiredStatus = (status) => status === 'hired';

export const staffIdOf = (app) =>
  app?.hired_staff_id || app?.hired_staff_user || null;

export const queueForApplication = (app) => {
  if (!app) return QUEUE_ACTIVE;
  if (app.status === 'hired') return QUEUE_HIRED;
  if (isClosedStatus(app.status)) return QUEUE_ALL;
  return QUEUE_ACTIVE;
};

export const filterByQueue = (apps, queueId) => {
  switch (queueId) {
    case QUEUE_HIRED:
      return apps.filter((a) => a.status === 'hired');
    case QUEUE_ALL:
      return apps;
    case QUEUE_ACTIVE:
    default:
      return apps.filter((a) => !isClosedStatus(a.status));
  }
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const filterByTriage = (apps, triageId, now = new Date()) => {
  switch (triageId) {
    case 'awaiting_candidate':
      return apps.filter((a) => ['draft', 'accepted'].includes(a.status));
    case 'blocked':
      return apps.filter((a) => a.status === 'blocked');
    case 'ready_to_hire':
      return apps.filter((a) => a.status === 'ready_to_hire');
    case 'starting_this_week': {
      const today = startOfDay(now);
      const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      return apps.filter((a) => {
        if (!a.offered_start_date) return false;
        const d = startOfDay(a.offered_start_date);
        return d >= today && d <= weekFromNow;
      });
    }
    case TRIAGE_ANY:
    default:
      return apps;
  }
};

export const filterOnboardingApplications = (
  apps,
  { queueId = QUEUE_ACTIVE, triageId = TRIAGE_ANY, searchTerm = '', now = new Date() } = {},
) => {
  let rows = filterByQueue(apps, queueId);
  if (queueId === QUEUE_ACTIVE) {
    rows = filterByTriage(rows, triageId, now);
  }
  if (searchTerm.trim()) {
    const q = searchTerm.toLowerCase();
    rows = rows.filter((a) => (
      (a.full_name || '').toLowerCase().includes(q)
      || (a.preferred_name || '').toLowerCase().includes(q)
      || (a.public_ref || '').toLowerCase().includes(q)
      || (a.personal_email || '').toLowerCase().includes(q)
      || (staffIdOf(a) || '').toLowerCase().includes(q)
    ));
  }
  return rows;
};

export const queueCounts = (apps) => ({
  [QUEUE_ACTIVE]: filterByQueue(apps, QUEUE_ACTIVE).length,
  [QUEUE_HIRED]: filterByQueue(apps, QUEUE_HIRED).length,
  [QUEUE_ALL]: apps.length,
});

/**
 * Keep the selected row inside the current filter. Prefer the existing
 * selection when it still matches; otherwise first visible row or none.
 */
export const reconcileSelection = (filteredApps, selectedRef) => {
  if (selectedRef && filteredApps.some((a) => a.public_ref === selectedRef)) {
    return selectedRef;
  }
  return filteredApps[0]?.public_ref || null;
};

const hasDisplayDate = (formattedStart) =>
  Boolean(formattedStart && formattedStart !== '—' && formattedStart !== 'N/A');

/**
 * Scan line for a left-pane row. Candidate refs stay out of the list —
 * they are searchable and shown in the detail header.
 *
 * Active: track + start date (what work this is, when it is due).
 * Hired:  staff ID + started date (the lookup keys after materialisation).
 */
export const formatCandidateListMeta = (
  app,
  { trackLabel = '', startDate = '' } = {},
) => {
  const hired = isHiredStatus(app?.status);
  const staffId = staffIdOf(app);
  const dated = hasDisplayDate(startDate);

  if (hired) {
    return {
      lead: staffId || trackLabel || null,
      trail: dated ? `Started ${startDate}` : null,
    };
  }

  return {
    lead: trackLabel || null,
    trail: dated ? `Starts ${startDate}` : 'Start date not set',
  };
};

/**
 * Date-only SLA chip. StatusBadge already covers blocked / ready / draft —
 * repeating those as chips is noise.
 */
export const startUrgency = (app, now = new Date()) => {
  if (!app || isHiredStatus(app.status) || !app.offered_start_date) return null;
  const today = startOfDay(now);
  const target = startOfDay(app.offered_start_date);
  const days = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 0) return { label: `Start was ${Math.abs(days)}d ago`, tone: 'red' };
  if (days === 0) return { label: 'Starts today', tone: 'red' };
  if (days <= 3) return { label: `Starts in ${days}d`, tone: 'red' };
  if (days <= 7) return { label: `Starts in ${days}d`, tone: 'amber' };
  return null;
};
